import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useToast } from '../contexts/ToastContext';
import { trackGuestMergeComplete } from '../utils/guestFunnelAnalytics';
import '../styles/FirstMeetingGuestPage.css';

/**
 * Phase 1: after login/register when guest vault merge was deferred (ENABLE_FIRST_MEETING_FLOW).
 * User chooses whether to attach meeting location before contacts are created from the vault.
 */
const FirstMeetingGuestPage = () => {
  const navigate = useNavigate();
  const { showSuccess, showError, showInfo } = useToast();
  const [submitting, setSubmitting] = useState(false);

  const finishMerge = async (payload) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await api.post('/api/guest/complete-merge', payload);
      const data = res.data;
      if (!data?.ok) {
        showError('Could not save your contacts. Please try again.');
        return;
      }
      if (data.alreadyCompleted) {
        showInfo('Your saved contacts are already in your account.');
      } else if (data.guestMerge) {
        trackGuestMergeComplete(data.guestMerge);
        const gm = data.guestMerge;
        if (gm.imported > 0 && gm.failed > 0) {
          showInfo(
            `We added ${gm.imported} contact${gm.imported === 1 ? '' : 's'}, but ${gm.failed} couldn’t finish syncing. Nothing was deleted — try again later.`
          );
        } else if (gm.imported > 0) {
          showSuccess(
            `${gm.imported} contact${gm.imported === 1 ? '' : 's'} from your save link ${gm.imported === 1 ? 'was' : 'were'} added to your list.`
          );
        } else if (gm.failed > 0) {
          showInfo(`Some saves couldn’t finish syncing (${gm.failed}). Try again later.`);
        }
      }
      navigate('/contacts', { replace: true, state: { fromFirstMeeting: true } });
    } catch (err) {
      const msg = err.response?.data?.error;
      if (msg === 'feature_disabled') {
        showError('This step is not available. You can open Contacts to see your list.');
        navigate('/contacts', { replace: true });
        return;
      }
      showError(typeof msg === 'string' ? msg : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveWithLocation = () => {
    if (!navigator.geolocation) {
      showInfo('Location isn’t available on this device. Saving contacts without meeting location.');
      finishMerge({ saveMeetingLocation: false });
      return;
    }
    setSubmitting(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setSubmitting(false);
        finishMerge({
          saveMeetingLocation: true,
          lat: latitude,
          lng: longitude,
        });
      },
      () => {
        setSubmitting(false);
        showInfo('Location wasn’t available. Saving contacts without meeting location.');
        finishMerge({ saveMeetingLocation: false });
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 300000 }
    );
  };

  const handleSkipLocation = () => {
    finishMerge({ saveMeetingLocation: false });
  };

  return (
    <div className="first-meeting-guest-page">
      <div className="first-meeting-guest-card">
        <h1 className="first-meeting-guest-title">Save meeting location?</h1>
        <p className="first-meeting-guest-copy">
          Choose whether to add where you met to this connection.
          You can change location sharing anytime in{' '}
          <Link to="/privacy-settings">Privacy Settings</Link>. If you skip, no meeting location is stored for
          either side.
        </p>
        <div className="first-meeting-guest-actions">
          <button
            type="button"
            className="first-meeting-guest-btn first-meeting-guest-btn-primary"
            onClick={handleSaveWithLocation}
            disabled={submitting}
          >
            Save with meeting location
          </button>
          <button
            type="button"
            className="first-meeting-guest-btn first-meeting-guest-btn-secondary"
            onClick={handleSkipLocation}
            disabled={submitting}
          >
            Continue without location
          </button>
        </div>
      </div>
    </div>
  );
};

export default FirstMeetingGuestPage;
